import { DisabledHelp } from '@vartion/ui'
import { acceptHMRUpdate, defineStore } from 'pinia'

import { checkCurrentRoute } from '~/router-helpers.ts'
import { bus } from '~/services/bus.ts'
import { IndexedDBCache } from '~/services/cache/IndexedDBCache.ts'
import {
  type DataSource,
  type Group,
  type IntelligentCheckJob,
  type Organization,
  type OrganizationPolicy,
  type RiskConfig,
  type Label,
  type UnitLimitType,
  type UnitLimitsUpdatedEvent,
  type User,
} from '~/types.ts'
import { useLabels } from './labels.ts'

const switchQueue: { resolve: () => void; reject: () => void; id: number }[] = []
let handling = false

let listeningOnId: undefined | number = undefined

function unitLimitHelpMessage(limitType: UnitLimitType): string {
  return {
    unit_limit_searches: $t('you-have-reached-the-maximum-number-of-searches-that-can-be-performed'),
    unit_limit_case_creations: $t('you-have-reached-the-maximum-number-of-case-creations-allowed'),
    unit_limit_active_cases: $t('you-have-reached-the-maximum-number-of-cases-allowed'),
    unit_limit_identity_verifications: $t('you-have-reached-the-maximum-number-of-identity-verifications-that-can-be-performed'),
    unit_limit_client_creations: $t('you-have-reached-the-maximum-number-of-clients-that-can-be-created'),
    unit_limit_form_creations: $t('you-have-reached-the-maximum-number-of-forms-that-can-be-sent'),
    unit_limit_transaction_creations: $t('you-have-reached-the-maximum-number-of-transactions-that-can-be-processed'),
    unit_limit_active_clients: $t('you-have-reached-the-maximum-number-of-active-clients-allowed'),
    unit_limit_active_form_templates: $t('you-have-reached-the-maximum-number-of-active-form-templates-allowed'),
    unit_limit_active_data_directory_entries: $t('you-have-reached-the-maximum-number-of-active-data-directory-entries-allowed'),
    unit_limit_active_users: $t('you-have-reached-the-maximum-number-of-users-allowed'),
  }[limitType]
}

function unitLimitDisabledHelp(organization: Organization, unitLimits: Partial<Record<UnitLimitType, number>>): DisabledHelp {
  const disabledHelp = new DisabledHelp()

  Object.entries(unitLimits).forEach(([limitType, count]) => {
    const type = limitType as UnitLimitType

    disabledHelp.add(unitLimitHelpMessage(type), getUnitLimitUsage(organization, type) + (count ?? 0) > getActiveUnitLimit(organization, type))
  })

  return disabledHelp
}

export const useOrganization = defineStore('organization', {
  state: (): Organization => ({
    id: 0,
    name: '',
    hyperline_customer_id: null,
    hyperline_subscription_template: null,
    products_editable: false,
    address: '',
    country: '',
    industry: '',
    city: '',
    postal_code: '',
    created_at: '',
    updated_at: '',
    last_activity_at: '',
    contract_start: '',
    contract_end: null,
    subscription_initial_billing_at: null,
    unit_limit_period: null,
    unit_limit_period_started_at: null,
    unit_limit_period_ends_at: null,
    origin: null,
    has_onboarding: false,
    has_implicit_feedback: false,
    has_data_directory: false,
    has_passport_verification: false,
    has_datachecker: false,
    has_transaction_monitoring: false,
    deleted_at: null,
    unit_limit_searches: null,
    unit_limit_case_creations: null,
    unit_limit_active_cases: null,
    unit_limit_identity_verifications: null,
    unit_limit_client_creations: null,
    unit_limit_form_creations: null,
    unit_limit_transaction_creations: null,
    unit_limit_active_clients: null,
    unit_limit_active_form_templates: null,
    unit_limit_active_data_directory_entries: null,
    unit_limit_active_users: null,
    unit_limit_usage: {},
    account_access_consent: true,
    essential_tier_vendors: [],
    third_party_tier_vendors: [],
    ai_tier_vendors: [],
    support_plan: 'free',
    has_veriff: undefined,
    has_hubspot: undefined,
    signup_token: null,
  }),

  getters: {
    canUseUnitLimits: (state) => {
      return (unitLimits: Partial<Record<UnitLimitType, number>>) => unitLimitDisabledHelp(state, unitLimits)
    },

    canSearch(): DisabledHelp {
      const disabledHelp = new DisabledHelp()
      disabledHelp.add(
        $t('you-have-reached-the-maximum-number-of-searches-that-can-be-performed'),
        getUnitLimitUsage(this, 'unit_limit_searches') >= getActiveUnitLimit(this, 'unit_limit_searches'),
      )

      return disabledHelp
    },

    canCreateCase(): DisabledHelp {
      return this.canCreateCases(1)
    },

    canCreateClient(): DisabledHelp {
      return this.canCreateClients(1)
    },

    canCreateClients: (state) => {
      return (count: number) =>
        unitLimitDisabledHelp(state, {
          unit_limit_client_creations: count,
          unit_limit_active_clients: count,
        })
    },

    canCreateFormTemplate(): DisabledHelp {
      return this.canCreateFormTemplates(1)
    },

    canCreateFormTemplates: (state) => {
      return (count: number) =>
        unitLimitDisabledHelp(state, {
          unit_limit_active_form_templates: count,
        })
    },

    canSendForm(): DisabledHelp {
      return this.canSendForms(1)
    },

    canSendForms: (state) => {
      return (count: number) =>
        unitLimitDisabledHelp(state, {
          unit_limit_form_creations: count,
        })
    },

    canProcessTransaction(): DisabledHelp {
      return this.canProcessTransactions(1)
    },

    canProcessTransactions: (state) => {
      return (count: number) =>
        unitLimitDisabledHelp(state, {
          unit_limit_transaction_creations: count,
        })
    },

    canInviteUser(): DisabledHelp {
      return this.canInviteUsers(1)
    },

    canInviteUsers: (state) => {
      return (count: number) =>
        unitLimitDisabledHelp(state, {
          unit_limit_active_users: count,
        })
    },

    canCreateCases: (state) => {
      return (count: number) => {
        const disabledHelp = new DisabledHelp()

        disabledHelp.add(
          $t('you-have-reached-the-maximum-number-of-case-creations-allowed'),
          getUnitLimitUsage(state, 'unit_limit_case_creations') + count > getActiveUnitLimit(state, 'unit_limit_case_creations'),
        )

        disabledHelp.add(
          $t('you-have-reached-the-maximum-number-of-searches-that-can-be-performed'),
          getUnitLimitUsage(state, 'unit_limit_searches') + count > getActiveUnitLimit(state, 'unit_limit_searches'),
        )

        disabledHelp.add(
          $t('you-have-reached-the-maximum-number-of-cases-allowed'),
          getUnitLimitUsage(state, 'unit_limit_active_cases') + count > getActiveUnitLimit(state, 'unit_limit_active_cases'),
        )

        return disabledHelp
      }
    },
  },

  actions: {
    async load() {
      const user = stores.user.user
      const organizationId = await IndexedDBCache.tags('organization').get(user.id)

      let switchable = false
      if (organizationId) {
        if (user.organizations.some((organization) => organization.id === organizationId && isOrganizationValid(organization))) {
          switchable = true
        }
        if (await IndexedDBCache.tags('globals').get('inGhostMode')) {
          switchable = true
        }
      }

      if (switchable) {
        await this.switch(organizationId)
      } else if (user.organizations.length >= 1) {
        const organization = user.organizations.find((organization) => isOrganizationValid(organization))
        if (organization) {
          await this.switch(organization.id)
        } else {
          return
        }
      } else if (user.organizations.length === 0) {
        return
      }

      await IndexedDBCache.tags('organization').put(user.id, this.id)
    },

    async fetch(id: number) {
      await stores.loading.update({ organization: true })

      const currentId = this.id
      try {
        api.headers.OrganizationId = id
        const { data } = await api.get(`organizations/${id}`)
        this.$state = data.organization
        stores.organizationFunctions.refresh()
        app.config.globalProperties.$organization = this
        stores.groups.set(data.groups)
        stores.users.set(data.users)
        Object.assign(stores.policies.policies, data.policies)
        stores.dataSources.dataSources = data.dataSources
        Object.assign(stores.settings.organizationDefaultSettings, data.defaultSettings)
        stores.users.setDeleted(data.deleted_users)
        this.listenForEvents()

        await this.cacheGhostMode()
      } catch (error) {
        api.headers.OrganizationId = currentId
        IndexedDBCache.tags('organization').flush()
        await $router.replace('/')
        await stores.loading.update({ organization: false })

        throw error
      }
      await stores.loading.update({ organization: false })
    },

    async switchWithGhostMode(id: number) {
      if (this.id === id) {
        return
      }

      if (stores.user.user.organizations.some((organization) => organization.id === id)) {
        await this.switch(id)

        return
      }

      if (!$can('administrations.ghostMode')) {
        await $router.replace('/')

        return
      }

      try {
        await $confirm({
          title: $t('enter-ghost-mode'),
          message: $t('are-you-sure-you-want-to-enter-ghost-mode'),
        })
      } catch {
        $router.replace('/')

        return
      }

      await this.switch(id)
    },

    async switch(id: number) {
      const promise = new Promise<void>((resolve, reject) => switchQueue.push({ resolve, reject, id }))

      this.handleSwitchQueue()

      await promise
    },

    async handleSwitchQueue() {
      if (handling) {
        return
      }
      handling = true

      while (switchQueue.length > 0) {
        const item = switchQueue.shift()
        if (item) {
          if (item.id === this.id) {
            item.resolve()
            continue
          }

          try {
            await this.fetch(item.id)
            await stores.user.refresh()
            await checkCurrentRoute()

            item.resolve()
          } catch {
            item.reject()
          }
        }
      }

      handling = false
    },

    async update(organization: Partial<Organization>) {
      const { data } = await api.patch<Organization>(`organizations/${this.id}`, organization)
      this.$state = data

      const userOrganization = stores.user.user.organizations.find((organization) => organization.id === this.id)
      if (userOrganization) {
        Object.assign(userOrganization, data)
      }
    },

    listenForEvents() {
      if (!window.echo || !this.id) {
        return
      }

      if (listeningOnId) {
        echo.leave(`App.Models.Organization.${listeningOnId}`)
      }

      echo
        .join(`App.Models.Organization.${this.id}`)
        .here((users: User[]) => (stores.users.onlineUsers = new Set(users.map((user) => user.id))))
        .leaving((user: User) => stores.users.onlineUsers.delete(Number(user.id)))
        .joining((user: User) => stores.users.onlineUsers.add(Number(user.id)))
        .listen('DataSourceCreated', ({ dataSource }: { dataSource: DataSource }) => stores.dataSources.dataSources.push(dataSource))
        .listen('DataSourcesUpdated', ({ dataSources }: { dataSources: DataSource[] }) => (stores.dataSources.dataSources = dataSources))
        .listen('OrganizationUpdated', ({ organization }: { organization: Organization }) => {
          Object.assign(this.$state, organization)

          const userOrganization = stores.user.user.organizations.find((organizationItem) => organizationItem.id === organization.id)
          if (userOrganization) {
            Object.assign(userOrganization, organization)
          }
        })
        .listen('OrganizationPolicyUpdated', ({ organizationPolicy }: { organizationPolicy: OrganizationPolicy }) => {
          Object.assign(stores.policies.policies, organizationPolicy)
        })
        .listen('OrganizationRiskConfigUpdated', ({ organizationRiskConfig }: { organizationRiskConfig: RiskConfig }) => {
          Object.assign(stores.riskConfig.config, organizationRiskConfig)
          Object.assign(stores.riskConfig.saved, copyObject(organizationRiskConfig))
        })
        .listen('DataDirectoryEntriesUpdated', () => bus.emit('dataDirectoryEntriesUpdated'))
        .listen('CasesRestored', () => bus.emit('cases.fetch'))
        .listen('GroupCreated', ({ group }: { group: Group & { users: User[] } }) => {
          const { users, ...groupData } = group
          stores.groups.groups[group.id] = groupData
          stores.groups.updateUsers(group.id, users)
        })
        .listen('GroupUpdated', ({ group }: { group: Group & { users: User[] } }) => {
          const { users, ...groupData } = group
          stores.groups.groups[group.id] = groupData
          stores.groups.updateUsers(group.id, users)
        })
        .listen('GroupDeleted', ({ id }: { id: number }) => {
          delete stores.groups.groups[id]

          for (const user of Object.values(stores.users.users)) {
            user.groups = user.groups.filter((group) => group.id !== id)
          }

          stores.user.user.groups = stores.user.user.groups.filter((group) => group.id !== id)
        })
        .listen('IntelligentCheckJobRunning', ({ job }: { job: IntelligentCheckJob }) => {
          stores.runningIntelligentCheckJobs.add(job)
        })
        .listen('IntelligentCheckJobFinished', ({ job }: { job: IntelligentCheckJob }) => {
          stores.runningIntelligentCheckJobs.remove(job)
        })
        .listen('UnitLimitsUpdated', ({ unitLimits, unitLimitPeriodStartedAt, unitLimitPeriodEndsAt }: UnitLimitsUpdatedEvent) => {
          const limits: Partial<Record<UnitLimitType, number | null>> = {}
          const unitLimitUsage: Partial<Record<UnitLimitType, number>> = {}

          Object.entries(unitLimits).forEach(([limitType, state]) => {
            const type = limitType as UnitLimitType
            limits[type] = state.limit
            unitLimitUsage[type] = state.usage
          })

          Object.assign(this.$state, limits)
          this.$state.unit_limit_usage = {
            ...this.$state.unit_limit_usage,
            ...unitLimitUsage,
          }
          this.$state.unit_limit_period_started_at = unitLimitPeriodStartedAt
          this.$state.unit_limit_period_ends_at = unitLimitPeriodEndsAt
        })
        .listen('LabelUpdated', ({ label }: { label: Label }) => {
          const labelsStore = useLabels()
          labelsStore.localUpdate(label)
        })

      listeningOnId = this.id
    },

    async cacheGhostMode() {
      const ghostMode = await IndexedDBCache.tags('globals').get('ghostMode')
      if (this.id !== ghostMode) {
        if (can('administrations.ghostMode') && this.account_access_consent) {
          IndexedDBCache.tags('globals').put('ghostMode', this.id)
        } else {
          IndexedDBCache.tags('globals').put('ghostMode', false)
        }
      }

      if (!app.config.globalProperties.$inGhostMode) {
        return
      }

      // Cache recent organizations that were accessed in ghost mode.
      const recents = await IndexedDBCache.tags('globals').get<number[], number[]>('recentAdministrationOrganizations', [])
      if (recents.includes(this.id)) {
        return
      }

      recents.push(this.id)
      if (recents.length > 3) {
        recents.splice(0, 1)
      }
      IndexedDBCache.tags('globals').put('recentAdministrationOrganizations', recents)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrganization, import.meta.hot))
}
